<div class="kyc-pre-review" data-testid="kyc-pre-review-modal">
  <h2 mat-dialog-title class="pr-title">
    <span class="pr-title-icon" [class.ok]="allMatch" aria-hidden="true">
      <mat-icon>{{ allMatch ? 'verified_user' : 'fact_check' }}</mat-icon>
    </span>
    <span class="pr-title-text">{{ 'KycPreReviewTitle' | translate }}</span>
  </h2>

  <!-- ============================================================= -->
  <!-- "This isn't my document" — shared explanation escape hatch    -->
  <!-- ============================================================= -->
  <ng-container *ngIf="showExplanation">
    <mat-dialog-content class="pr-content">
      <p class="pr-explain-prompt">{{ 'KycPreReviewExplainPrompt' | translate }}</p>
      <mat-form-field appearance="outline" class="pr-explain-field">
        <mat-label>{{ 'KycPreReviewExplainLabel' | translate }}</mat-label>
        <textarea
          matInput
          rows="3"
          [(ngModel)]="explanation"
          data-testid="kyc-pre-review-explanation"
        ></textarea>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions class="pr-actions">
      <button
        mat-flat-button
        color="primary"
        class="pr-primary"
        cdkFocusInitial
        (click)="restart()"
        data-testid="kyc-pre-review-restart"
      >
        <mat-icon aria-hidden="true">photo_camera</mat-icon>
        <span>{{ 'KycPreReviewRestart' | translate }}</span>
      </button>
      <button
        mat-button
        class="pr-secondary danger"
        [disabled]="!explanation.trim()"
        (click)="proceedAnyway()"
        data-testid="kyc-pre-review-proceed"
      >
        {{ 'KycPreReviewProceedAnyway' | translate }}
      </button>
    </mat-dialog-actions>
  </ng-container>

  <!-- ============================================================= -->
  <!-- NO-QR path — "Select the correct data" stepper               -->
  <!-- ============================================================= -->
  <ng-container *ngIf="!showExplanation && allowAmend">
    <mat-dialog-content class="pr-content" data-testid="kyc-pre-review-stepper">
      <p class="pr-lead">{{ 'KycPreReviewStepLead' | translate }}</p>

      <!-- One field to reconcile at a time -->
      <ng-container *ngIf="currentStep as step">
        <p class="pr-step-progress" data-testid="kyc-pre-review-step-progress">
          {{ 'KycPreReviewStepProgress' | translate: { n: stepNumber, t: totalSteps } }}
        </p>

        <div class="pr-step" [attr.data-testid]="'kyc-pre-review-step-' + step.key">
          <h3 class="pr-step-title">{{ step.label | translate }}</h3>
          <p class="pr-step-prompt">{{ 'KycPreReviewStepPrompt' | translate }}</p>

          <mat-radio-group
            class="pr-choices"
            [ngModel]="choice[step.key]"
            (ngModelChange)="selectChoice(step.key, $event)"
          >
            <mat-radio-button
              *ngIf="optionAvailable(step.key, 'doc')"
              value="doc"
              class="pr-choice"
              data-testid="kyc-pre-review-opt-doc"
            >
              <span class="pr-choice-label">{{ 'KycPreReviewOptDoc' | translate }}</span>
              <span class="pr-choice-value">{{ step.docValue }}</span>
            </mat-radio-button>

            <mat-radio-button
              *ngIf="optionAvailable(step.key, 'account')"
              value="account"
              class="pr-choice"
              data-testid="kyc-pre-review-opt-account"
            >
              <span class="pr-choice-label">{{ 'KycPreReviewOptAccount' | translate }}</span>
              <span class="pr-choice-value">{{ step.userValue }}</span>
            </mat-radio-button>

            <mat-radio-button
              value="manual"
              class="pr-choice"
              data-testid="kyc-pre-review-opt-manual"
            >
              <span class="pr-choice-label">{{ 'KycPreReviewOptManual' | translate }}</span>
            </mat-radio-button>
          </mat-radio-group>

          <ng-container *ngIf="choice[step.key] === 'manual'">
            <!-- DOB: shared date widget -->
            <app-input-date
              *ngIf="step.key === 'dob'"
              [ngModel]="manualDobDate"
              (ngModelChange)="onManualDobChange($event)"
              [placeholder]="step.label | translate"
              data-testid="kyc-pre-review-manual-input"
            ></app-input-date>

            <!-- CPF: shared masked/validated widget (no leak check) -->
            <app-question-cpf
              *ngIf="step.key === 'cpf'"
              [question]="manualCpfQuestion"
              (changeAnswer)="onManualCpfChange($event)"
              data-testid="kyc-pre-review-manual-input"
            ></app-question-cpf>

            <!-- Name: plain text -->
            <mat-form-field
              *ngIf="step.key === 'name'"
              appearance="outline"
              class="pr-edit-field"
            >
              <mat-label>{{ step.label | translate }}</mat-label>
              <input
                matInput
                [(ngModel)]="manual[step.key]"
                data-testid="kyc-pre-review-manual-input"
              />
            </mat-form-field>
          </ng-container>
        </div>
      </ng-container>

      <!-- Fallback: modal opened but nothing actually needs a decision -->
      <p class="pr-consent" *ngIf="!currentStep">{{ 'KycPreReviewStepAllGood' | translate }}</p>
    </mat-dialog-content>

    <mat-dialog-actions class="pr-actions">
      <button
        *ngIf="currentStep && !isFirstStep"
        mat-button
        class="pr-secondary"
        (click)="back()"
        data-testid="kyc-pre-review-back"
      >
        {{ 'KycPreReviewStepBack' | translate }}
      </button>
      <button
        mat-flat-button
        color="primary"
        class="pr-primary"
        cdkFocusInitial
        [disabled]="!canAdvance()"
        (click)="next()"
        [attr.data-testid]="isLastStep ? 'kyc-pre-review-finish' : 'kyc-pre-review-next'"
      >
        <mat-icon aria-hidden="true">{{ isLastStep ? 'check' : 'arrow_forward' }}</mat-icon>
        <span>{{ (isLastStep ? 'KycPreReviewStepFinish' : 'KycPreReviewStepNext') | translate }}</span>
      </button>
      <button mat-button class="pr-secondary" (click)="reject()" data-testid="kyc-pre-review-reject">
        {{ 'KycPreReviewReject' | translate }}
      </button>
    </mat-dialog-actions>
  </ng-container>

  <!-- ============================================================= -->
  <!-- QR path — strict confirm view (unchanged behaviour)          -->
  <!-- ============================================================= -->
  <ng-container *ngIf="!showExplanation && !allowAmend">
    <mat-dialog-content class="pr-content">
      <p class="pr-lead">
        {{ (allMatch ? 'KycPreReviewLeadMatch' : 'KycPreReviewLeadMismatch') | translate }}
      </p>

      <ul class="pr-fields" role="list" data-testid="kyc-pre-review-table">
        <li
          class="pr-field"
          *ngFor="let f of fields"
          [ngClass]="f.status"
          [attr.data-testid]="'kyc-pre-review-row-' + f.key"
        >
          <div class="pr-field-top">
            <span class="pr-field-label">{{ f.label | translate }}</span>
            <span
              class="pr-field-status"
              [attr.data-testid]="'kyc-pre-review-status-' + f.key"
              [attr.aria-label]="(f.status === 'match' ? 'KycPreReviewA11yMatches' : 'KycPreReviewA11yDiffers') | translate"
            >
              <mat-icon aria-hidden="true">{{ f.status === 'match' ? 'check_circle' : 'sync_alt' }}</mat-icon>
            </span>
          </div>
          <div class="pr-field-doc">{{ f.docValue }}</div>
          <div class="pr-field-user" *ngIf="f.status !== 'match'">
            {{ 'KycPreReviewOnFile' | translate }}: <span class="pr-user-value">{{ f.userValue }}</span>
          </div>
        </li>
      </ul>

      <p class="pr-consent" *ngIf="!allMatch">{{ 'KycPreReviewConsent' | translate }}</p>
    </mat-dialog-content>

    <mat-dialog-actions class="pr-actions">
      <button
        mat-flat-button
        color="primary"
        class="pr-primary"
        cdkFocusInitial
        (click)="confirm()"
        data-testid="kyc-pre-review-confirm"
      >
        <mat-icon aria-hidden="true">check</mat-icon>
        <span>{{ (allMatch ? 'KycPreReviewConfirmMatch' : 'KycPreReviewConfirmAdopt') | translate }}</span>
      </button>
      <button mat-button class="pr-secondary" (click)="reject()" data-testid="kyc-pre-review-reject">
        {{ 'KycPreReviewReject' | translate }}
      </button>
    </mat-dialog-actions>
  </ng-container>
</div>
